// A comic page: hand-drawn panels laid out on a column grid instead of being
// stacked edge to edge. The layout owns the panel sizes, so a wide beat and a
// quiet one read differently; each drawing is then scaled to fit the frame it
// was given. Pass a different strip to reuse the component elsewhere.

import { publicUrl } from "../data/paths";

export interface ComicPanel {
  /** Path under site/public, without a leading slash. */
  src: string;
  alt: string;
  /** Intrinsic size of the trimmed drawing, in source pixels. */
  width: number;
  height: number;
  /** Columns this panel covers, out of the strip's column count. */
  columns: number;
  /** Rows it covers; one unless a beat is worth the extra height. */
  rows?: number;
  /** Share of the reader's stage height. Talky scenes claim all of it. */
  scale?: number;
  /** How the drawing meets its frame. Contain never crops a speech bubble. */
  fit?: "contain" | "cover";
  /** Where the drawing sits in the frame, as an object-position value. */
  position?: string;
}

export interface ComicStrip {
  /** Accessible name for the whole page of panels. */
  label: string;
  /** Column count the panel spans are expressed in. */
  columns: number;
  /** Row track sizes, e.g. ["1fr", "1fr", "1.15fr"]. */
  rows: string[];
  panels: ComicPanel[];
}

/** The landing page's strip: eleven panels, three rows. Spans follow each
 *  trimmed drawing's proportions, so the page tiles without white margins. */
export const RECOVERY_COMIC: ComicStrip = {
  label: "Two researchers spend their last labels on a robot that catches fire",
  columns: 24,
  rows: ["1fr", "1fr", "1.15fr"],
  panels: [
    {
      src: "illustrations/comic/scene-01.webp",
      alt: "A man winds up a small robot and says: there, now let’s see how it does. A woman says: great, I have one spare label left.",
      width: 1216,
      height: 889,
      columns: 6,
    },
    {
      src: "illustrations/comic/scene-02.webp",
      alt: "The man holds up his own label and says: good, I got a spare one too.",
      width: 1149,
      height: 888,
      columns: 5,
    },
    {
      src: "illustrations/comic/scene-03.webp",
      alt: "The robot picks up a lighter. The woman asks: wait, is that a lighter? That was not in the training set.",
      width: 1241,
      height: 839,
      columns: 7,
    },
    {
      src: "illustrations/comic/scene-04.webp",
      alt: "The man holds the woman back: let it do its thing. We only have a few labels left. Let it discover what we didn’t train it for. She answers: are you sure?",
      width: 1296,
      height: 886,
      columns: 6,
    },
    {
      src: "illustrations/comic/scene-05.webp",
      alt: "The man explains: think about it, its own actions can push it into states the expert demonstrations never covered.",
      width: 1223,
      height: 872,
      columns: 6,
    },
    {
      src: "illustrations/comic/scene-06.webp",
      alt: "He continues: that’s life, that’s the real world. You can’t account for every disturbance, and you can’t perfectly predict how its own actions will change what happens next.",
      width: 1218,
      height: 909,
      columns: 6,
    },
    {
      src: "illustrations/comic/scene-07.webp",
      alt: "He concludes: let it make mistakes, and we use our spare labels on correcting those mistakes rather than preventing them. Behind him the robot has set itself alight.",
      width: 1208,
      height: 921,
      columns: 5,
    },
    {
      src: "illustrations/comic/scene-08.webp",
      alt: "The robot stands in a column of flame. The woman shouts: oh my god!",
      width: 1201,
      height: 781,
      columns: 7,
      scale: 0.72,
    },
    {
      src: "illustrations/comic/scene-09.webp",
      alt: "The robot has burned down to a smouldering heap while the two watch.",
      width: 1222,
      height: 689,
      columns: 9,
      scale: 0.74,
    },
    {
      src: "illustrations/comic/scene-10.webp",
      alt: "Nothing is left of the robot but a smoking pile of ash.",
      width: 1193,
      height: 665,
      columns: 9,
      scale: 0.7,
    },
    {
      src: "illustrations/comic/scene-11.webp",
      alt: "The man looks at the ash and says: I’ll build a new one.",
      width: 1118,
      height: 847,
      columns: 6,
      scale: 0.86,
    },
  ],
};

/** The drawing on its own, framed, at the size the caller asks for. */
export function comicArt(panel: ComicPanel, eager = false): HTMLImageElement {
  const image = document.createElement("img");
  image.className = "comic__art";
  image.src = publicUrl(panel.src);
  image.alt = panel.alt;
  image.width = panel.width;
  image.height = panel.height;
  image.loading = eager ? "eager" : "lazy";
  image.decoding = "async";
  if (panel.fit) image.style.objectFit = panel.fit;
  if (panel.position) image.style.objectPosition = panel.position;
  return image;
}

export function renderComicStrip(
  mount: HTMLElement,
  strip: ComicStrip = RECOVERY_COMIC,
): void {
  mount.replaceChildren(buildComicStrip(strip));
}

export function buildComicStrip(
  strip: ComicStrip = RECOVERY_COMIC,
): HTMLElement {
  const page = document.createElement("div");
  page.className = "comic";
  page.setAttribute("role", "group");
  page.setAttribute("aria-label", strip.label);
  page.style.setProperty("--comic-columns", String(strip.columns));
  page.style.setProperty("--comic-rows", strip.rows.join(" "));

  for (const panel of strip.panels) {
    const frame = document.createElement("figure");
    frame.className = "comic__panel";
    frame.style.setProperty("--comic-span", String(panel.columns));
    if (panel.rows && panel.rows > 1) {
      frame.style.setProperty("--comic-row-span", String(panel.rows));
    }
    frame.append(comicArt(panel));
    page.append(frame);
  }
  return page;
}
